import { useContext, useEffect, useMemo, useReducer } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'

import { parseQueryString, stringifyQueryString } from 'core/infra/queryString'

import { BID_STATUS } from 'legacy/constants/bids'
import { LoadBoardContext, LOAD_BOARD_ACTIONS } from 'legacy/contexts/loadBoard'
import * as bidService from 'legacy/services/bids'
import sentry from 'legacy/utils/sentry'

import { BidStatus, getBidBookingType } from 'loads/domain/Bid'
import { useBookingOffer } from 'loads/ui/hooks/useBookingOffer'
import { createBid } from 'loads/use-cases/bid/createBid'

import { CONTEXTS } from '../../OfferCard/OfferCard'

export class BidMalformedError extends Error {
  constructor(message = 'Invalid bid payload') {
    super(message)
    this.name = 'BidMalformed'
    this.code = 422
  }
}

export class OfferGoneError extends Error {
  constructor(message = 'Load or Offer is gone') {
    super(message)
    this.name = 'OfferGone'
    this.code = 410
  }
}

const ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_NOT_LOADING: 'SET_NOT_LOADING',
  SET_BID_SUBMITTED: 'SET_BID_SUBMITTED',
  SET_BID_ERROR: 'SET_BID_ERROR',
  SET_TAKEN: 'SET_TAKEN',
}

const initialState = {
  isLoading: false,
  bidSubmitted: false,
  bidError: false,
  taken: false,
}

export function reducer(currentState, action) {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return { ...currentState, isLoading: true }
    case ACTIONS.SET_NOT_LOADING:
      return { ...currentState, isLoading: false }
    case ACTIONS.SET_BID_SUBMITTED:
      return { ...currentState, bidSubmitted: true }
    case ACTIONS.SET_BID_ERROR:
      return { ...currentState, bidError: true }
    case ACTIONS.SET_TAKEN:
      return { ...currentState, taken: action.value }
    default:
      throw Error('invalid action type')
  }
}

export function useBidDialog() {
  const [
    { biddingBookItNowPrice, bidOfferUUID, bidLoadUUID, bids, bidLoad, carrier },
    loadboardDispatch,
  ] = useContext(LoadBoardContext)
  const { navigateToBookOffer } = useBookingOffer()
  const history = useHistory()

  const [{ isLoading, bidSubmitted, bidError, taken }, dispatch] = useReducer(reducer, initialState)

  const isTaken = Boolean(taken)
  const isOpen = Boolean(bidOfferUUID)
  const activeBid = useMemo(() => bids[bidLoadUUID], [bidLoadUUID, bids])
  const isCounterBid = Boolean(activeBid?.countered_bid)
  const bidProbabilityEnabled = Boolean(carrier.bid_probability)
  const isExpired = activeBid?.status === BID_STATUS.TIMED_OUT

  useEffect(() => {
    if (isOpen && [BidStatus.Awarded, BidStatus.Refused].includes(activeBid?.status)) {
      const bookingType = getBidBookingType(activeBid)
      navigateToBookOffer({ id: bidLoadUUID, triggerContext: CONTEXTS.BID_DIALOG, bookingType })
      loadboardDispatch({ type: LOAD_BOARD_ACTIONS.CLOSE_BID_DIALOG, payload: bidOfferUUID })
    }
  }, [
    activeBid,
    bidLoadUUID,
    isCounterBid,
    isOpen,
    navigateToBookOffer,
    loadboardDispatch,
    bidOfferUUID,
  ])

  async function placeBid(biddingPrice) {
    let bid = null

    dispatch({ type: ACTIONS.SET_LOADING })

    try {
      if (isCounterBid) {
        ;({ data: bid } = await bidService.counterBid(activeBid.uuid, { price: biddingPrice }))
      } else {
        bid = await createBid({
          bidProbability: bidProbabilityEnabled,
          price: biddingPrice,
          offerUUID: bidOfferUUID,
        })
      }

      loadboardDispatch({ type: LOAD_BOARD_ACTIONS.UPSERT_BID, payload: bid })
      dispatch({ type: ACTIONS.SET_BID_SUBMITTED })

      return bid
    } catch (error) {
      if (error?.response?.status === 422) {
        dispatch({ type: ACTIONS.SET_NOT_LOADING })
        throw new BidMalformedError(error.response?.data?.detail)
      }

      if (error?.response?.status === 410) {
        const detail = error.response?.data?.detail || ''
        if (detail.includes('accepted')) {
          dispatch({ type: ACTIONS.SET_TAKEN, value: 'load' })
          loadboardDispatch({
            type: LOAD_BOARD_ACTIONS.MARK_OFFER_TAKEN,
            payload: { loadUUID: bidLoadUUID },
          })
        } else {
          dispatch({ type: ACTIONS.SET_TAKEN, value: 'offer' })
        }
        throw new OfferGoneError(detail)
      }

      sentry.log(error)
      dispatch({ type: ACTIONS.SET_BID_ERROR })
    } finally {
      dispatch({ type: ACTIONS.SET_NOT_LOADING })
    }
  }

  async function refuseBid(bid) {
    try {
      dispatch({ type: ACTIONS.SET_LOADING })
      removeBid(bid)
      await bidService.refuseBid(bid.uuid)
      toast.success('Offer refused')
    } catch {
      toast.error('Failed to refuse offer')
    } finally {
      dispatch({ type: ACTIONS.SET_NOT_LOADING })
    }
  }

  async function deleteBid(bid) {
    if (!bid) return

    dispatch({ type: ACTIONS.SET_LOADING })
    try {
      await bidService.deleteBid(bid.uuid)
      setBidRemoved(bid)

      closeDialog()
    } catch (error) {
      dispatch({ type: ACTIONS.SET_BID_ERROR })
      throw error
    } finally {
      dispatch({ type: ACTIONS.SET_NOT_LOADING })
    }
  }

  function removeBid(bid) {
    if (!bid) return
    setBidRemoved(bid)
    loadboardDispatch({ type: LOAD_BOARD_ACTIONS.REMOVE_BID, payload: bid?.load?.uuid })
  }

  function setBidRemoved(bid) {
    bid.status = BID_STATUS.REMOVED
    loadboardDispatch({ type: LOAD_BOARD_ACTIONS.UPSERT_BID, payload: bid })
  }

  function closeDialog() {
    loadboardDispatch({ type: LOAD_BOARD_ACTIONS.CLOSE_BID_DIALOG, payload: bidOfferUUID })
  }

  function openDialog(loadOffer) {
    const { activeOffer } = loadOffer
    const { pathname, search } = history.location

    history.push(
      `${pathname}?${stringifyQueryString({
        ...parseQueryString(search),
        bidding: loadOffer?.uuid,
      })}`
    )
    return loadboardDispatch({
      type: LOAD_BOARD_ACTIONS.OPEN_BID_DIALOG,
      payload: {
        offerUUID: activeOffer?.uuid,
        price: activeOffer?.price,
        load: loadOffer,
      },
    })
  }

  return {
    activeBid,
    bidOfferUUID,
    bidLoadUUID,
    bidLoad,
    biddingBookItNowPrice,
    bidSubmitted,
    bidError,
    taken,
    isCounterBid,
    isLoading,
    isOpen,
    isTaken,
    isExpired,
    closeDialog,
    placeBid,
    refuseBid,
    deleteBid,
    removeBid,
    openDialog,
    setBidRemoved,
    bidProbabilityEnabled,
  }
}
